/**
 * Canonical Hebrew text utilities.
 *
 * IMPORTANT: This module must match the backend alignment_service.py exactly.
 * Any changes here should be mirrored in the Python backend.
 */

// Hebrew Unicode ranges
const NIQQUD_TAAMIM_RANGE = /[\u0591-\u05BD\u05BF-\u05C7]/g;
const HEBREW_LETTERS = /[\u0590-\u05FF]+/g;

// Hebrew punctuation characters
// eslint-disable-next-line no-unused-vars
const MAQAF = '\u05BE';  // Hebrew hyphen ־
// eslint-disable-next-line no-unused-vars
const GERESH = '\u05F3'; // ׳
// eslint-disable-next-line no-unused-vars
const GERSHAYIM = '\u05F4'; // ״

/**
 * Clean text for tokenization - matches backend clean_text()
 *
 * @param {string} text - Raw text to clean
 * @returns {string} - Cleaned text with normalized whitespace
 */
export function cleanText(text) {
  if (!text) return '';

  // Remove HTML tags (matches Python: re.sub(r'<[^>]+>', '', text))
  let cleaned = text.replace(/<[^>]+>/g, '');

  // Convert maqaf to space (KEY: this is what backend does!)
  // Matches Python: text = text.replace('\u05be', ' ')
  cleaned = cleaned.replace(/\u05be/g, ' ');

  // Remove niqqud/taamim using Unicode normalization
  // Matches Python: unicodedata.normalize('NFD', text)
  //                 ''.join(ch for ch in text if unicodedata.category(ch) != 'Mn')
  cleaned = cleaned.normalize('NFD');
  // Remove combining marks (Unicode category Mn - includes Latin diacritics)
  cleaned = cleaned.replace(/[\u0300-\u036f]/g, '');
  // Also remove Hebrew-specific combining marks
  cleaned = cleaned.replace(NIQQUD_TAAMIM_RANGE, '');
  cleaned = cleaned.normalize('NFC');

  // Strip non-Hebrew punctuation (matches Python: re.sub(r'[^\w\s\u0590-\u05FF]', ' ', text))
  cleaned = cleaned.replace(/[^\w\s\u0590-\u05FF]/g, ' ');

  // Normalize whitespace
  return cleaned.split(/\s+/).filter(w => w).join(' ');
}

/**
 * Normalize a single word - matches backend normalize_word()
 *
 * @param {string} word - Word to normalize
 * @returns {string} - Normalized word
 */
export function normalizeWord(word) {
  if (!word) return '';

  // Unicode normalization (matches Python NFD decomposition)
  let w = word.normalize('NFD');

  // Remove combining marks (niqqud/taamim)
  w = w.replace(/[\u0300-\u036f]/g, '');
  w = w.replace(NIQQUD_TAAMIM_RANGE, '');

  // Remove geresh/gershayim/quotes
  // Matches Python: re.sub(r"[׳״'\u05f3\u05f4]", '', no_marks)
  w = w.replace(/[׳״'\u05f3\u05f4]/g, '');

  return w.normalize('NFC');
}

/**
 * Get words from text - matches backend get_words()
 *
 * @param {string} text - Text to tokenize
 * @returns {string[]} - Array of normalized words
 */
export function getWords(text) {
  const cleaned = cleanText(text);
  // Extract only Hebrew letter sequences (matches Python: re.findall(r'[\u0590-\u05FF]+', cleaned))
  const words = cleaned.match(HEBREW_LETTERS) || [];
  return words.map(w => normalizeWord(w)).filter(w => w);
}

/**
 * Get raw tokens from text for display purposes.
 * Unlike getWords(), this preserves non-Hebrew words and doesn't normalize.
 *
 * @param {string} text - Text to tokenize
 * @returns {string[]} - Array of raw tokens
 */
export function getRawTokens(text) {
  if (!text) return [];
  // Remove HTML tags
  let cleaned = text.replace(/<[^>]+>/g, '');
  // Split on whitespace and maqaf
  return cleaned.split(/[\s\u05be]+/).filter(w => w.length > 0);
}

/**
 * Normalize quotes for comparison
 *
 * @param {string} s - String with quotes to normalize
 * @returns {string} - String with normalized quotes
 */
export function normalizeQuotes(s) {
  return s.replace(/׳/g, "'").replace(/״/g, '"').replace(/''/g, '"');
}

/**
 * Check if word is abbreviation (kitzur) of another
 * Matches backend is_kitzur()
 *
 * @param {string} str1 - First word
 * @param {string} str2 - Second word
 * @returns {boolean} - True if one is abbreviation of other
 */
export function isKitzur(str1, str2) {
  const s1 = normalizeQuotes(str1);
  const s2 = normalizeQuotes(str2);

  // Special case: don't match ח' as abbreviation
  if (s1 === "ח'" || s2 === "ח'") return false;

  // Both end with quote - check if prefixes match
  if (s1.endsWith("'") && s2.endsWith("'")) {
    const pos1 = s1.indexOf("'");
    const pos2 = s2.indexOf("'");
    if (pos1 > 0 && pos2 > 0) {
      return s1.slice(0, pos1) === s2.slice(0, pos2);
    }
  }

  // One ends with quote - check if it's abbreviation of the other
  if (s2.endsWith("'")) {
    const pos2 = s2.indexOf("'");
    if (pos2 > 0 && pos2 < s1.length) {
      return s1.startsWith(s2.slice(0, pos2));
    }
  }
  if (s1.endsWith("'")) {
    const pos1 = s1.indexOf("'");
    if (pos1 > 0 && pos1 < s2.length) {
      return s2.startsWith(s1.slice(0, pos1));
    }
  }
  return false;
}

/**
 * Check if words differ only by final mem/nun (ם/ן)
 * Matches backend is_mem_nun_variant()
 *
 * @param {string} s1 - First word
 * @param {string} s2 - Second word
 * @returns {boolean} - True if words differ only by final mem/nun
 */
export function isMemNunVariant(s1, s2) {
  if (s1.length !== s2.length || s1.length < 2) return false;
  if ((s1.endsWith('ם') && s2.endsWith('ן')) || (s1.endsWith('ן') && s2.endsWith('ם'))) {
    return s1.slice(0, -1) === s2.slice(0, -1);
  }
  return false;
}

/**
 * Fuzzy word matching - matches backend words_match_fuzzy()
 * Normalizes inputs before comparison for consistent matching.
 *
 * @param {string} w1 - First word (can be raw or normalized)
 * @param {string} w2 - Second word (can be raw or normalized)
 * @returns {boolean} - True if words match (exact or fuzzy)
 */
export function wordsMatchFuzzy(w1, w2) {
  // Normalize both words for comparison
  const n1 = normalizeWord(w1);
  const n2 = normalizeWord(w2);

  if (!n1 || !n2) return false;
  if (n1 === n2) return true;
  if (isKitzur(n1, n2)) return true;
  if (isMemNunVariant(n1, n2)) return true;

  // Check Hebrew prefixes (ו, ב, כ, ל, ה, ש, מ)
  const prefixes = ['ו', 'ב', 'כ', 'ל', 'ה', 'ש', 'מ'];
  for (const p of prefixes) {
    if (n1 === p + n2 || n2 === p + n1) return true;
    // Also check combinations like וב, וכ, etc.
    for (const p2 of prefixes) {
      if (n1 === p + p2 + n2 || n2 === p + p2 + n1) return true;
    }
  }
  return false;
}

/**
 * Find indices of shared words between two word arrays
 *
 * @param {string[]} words1 - First array of normalized words
 * @param {string[]} words2 - Second array of normalized words
 * @returns {{shared1: number[], shared2: number[]}} - Indices of shared words in each array
 */
export function findSharedWordIndices(words1, words2) {
  const shared1 = new Set();
  const shared2 = new Set();

  for (let i = 0; i < words1.length; i++) {
    for (let j = 0; j < words2.length; j++) {
      if (wordsMatchFuzzy(words1[i], words2[j])) {
        shared1.add(i);
        shared2.add(j);
      }
    }
  }
  return { shared1: [...shared1], shared2: [...shared2] };
}

/**
 * Check if a word at index is shared with another text's words
 *
 * @param {number} index - Word index to check
 * @param {string[]} words - Words array
 * @param {string[]} otherWords - Other text's words to compare against
 * @returns {boolean} - True if word is shared
 */
export function isWordShared(index, words, otherWords) {
  if (index < 0 || index >= words.length) return false;
  const word = words[index];
  return otherWords.some(otherWord => wordsMatchFuzzy(word, otherWord));
}
